import React, { useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Z_INDEX, zIndexClass } from "@/utils/z-index";

// Modal portal root ID - must match the id in arc-layout.tsx
const MODAL_ROOT_ID = "modal-root";

export interface DialogProps {
  open: boolean;
  onClose: () => void;
  children: React.ReactNode;
  size?: "sm" | "md" | "lg" | "xl" | "2xl" | "full";
  closeOnBackdropClick?: boolean;
  closeOnEscape?: boolean;
  className?: string;
}

export interface DialogHeaderProps {
  children: React.ReactNode;
  onClose?: () => void;
  className?: string;
}

export interface DialogBodyProps {
  children: React.ReactNode;
  className?: string;
}

export interface DialogFooterProps {
  children: React.ReactNode;
  className?: string;
}

/**
 * Arc-style Dialog/Modal component
 *
 * Features:
 * - Smooth open/close animations
 * - Backdrop blur effect (glassmorphism)
 * - Multiple sizes
 * - Keyboard escape to close
 * - Click outside to close (optional)
 * - Content-aware centering (respects sidebar layout)
 * - Compound components: DialogHeader, DialogBody, DialogFooter
 */
const Dialog: React.FC<DialogProps> & {
  Header: React.FC<DialogHeaderProps>;
  Body: React.FC<DialogBodyProps>;
  Footer: React.FC<DialogFooterProps>;
} = ({
  open,
  onClose,
  children,
  size = "md",
  closeOnBackdropClick = true,
  closeOnEscape = true,
  className = "",
}) => {
  // Handle escape key
  const handleEscape = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === "Escape" && closeOnEscape) {
        onClose();
      }
    },
    [onClose, closeOnEscape]
  );

  useEffect(() => {
    if (open) {
      document.addEventListener("keydown", handleEscape);
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "";
    };
  }, [open, handleEscape]);

  // Size styles
  const sizeStyles = {
    sm: "max-w-md",
    md: "max-w-xl",
    lg: "max-w-2xl",
    xl: "max-w-4xl",
    "2xl": "max-w-6xl",
    full: "max-w-[90vw] max-h-[90vh]",
  };

  const modalContent = (
    <AnimatePresence>
      {open && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={closeOnBackdropClick ? onClose : undefined}
            className={`absolute inset-0 ${zIndexClass(
              Z_INDEX.MODAL
            )} bg-black/40 backdrop-blur-sm pointer-events-auto`}
            aria-hidden="true"
          />

          {/* Dialog container */}
          <div
            className={`absolute inset-0 ${zIndexClass(
              Z_INDEX.MODAL
            )} flex items-center justify-center p-4 pointer-events-none`}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{
                type: "spring",
                damping: 25,
                stiffness: 300,
                duration: 0.2,
              }}
              role="dialog"
              aria-modal="true"
              className={`
                w-full ${sizeStyles[size]}
                bg-surface-tertiary
                rounded-arc-xl
                shadow-arc-2xl
                flex flex-col
                max-h-[90vh]
                overflow-hidden
                pointer-events-auto
                ${className}
              `}
            >
              {children}
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );

  if (typeof document === "undefined") {
    return null;
  }

  // Render in modal-root for content-aware centering, fallback to body
  const modalRoot = document.getElementById(MODAL_ROOT_ID) || document.body;
  return createPortal(modalContent, modalRoot);
};

/**
 * Dialog Header component
 */
const DialogHeader: React.FC<DialogHeaderProps> = ({
  children,
  onClose,
  className = "",
}) => {
  // Détecter si une couleur de texte personnalisée est passée
  const hasCustomTextColor = className.includes("text-");

  return (
    <div
      className={`
        flex items-center justify-between
        px-6 py-4
        border-b border-arc-border
        flex-shrink-0
        ${className}
      `}
    >
      <div
        className={`text-lg font-semibold ${
          hasCustomTextColor ? "" : "text-content-primary"
        }`}
      >
        {children}
      </div>
      {onClose && (
        <button
          type="button"
          onClick={onClose}
          className={`
            p-1.5 -mr-1.5
            rounded-arc-sm
            ${
              hasCustomTextColor
                ? "text-current opacity-80 hover:opacity-100"
                : "text-content-tertiary hover:text-content-primary hover:bg-surface-secondary"
            }
            transition-colors duration-arc-fast
            focus:outline-none focus-visible:ring-2 focus-visible:ring-accent
          `}
          aria-label="Close"
        >
          <svg
            className="w-5 h-5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      )}
    </div>
  );
};

/**
 * Dialog Body component
 */
const DialogBody: React.FC<DialogBodyProps> = ({
  children,
  className = "",
}) => {
  return (
    <div
      className={`
        flex-1
        overflow-y-auto
        px-6 py-4
        text-content-secondary
        ${className}
      `}
    >
      {children}
    </div>
  );
};

/**
 * Dialog Footer component
 */
const DialogFooter: React.FC<DialogFooterProps> = ({
  children,
  className = "",
}) => {
  return (
    <div
      className={`
        flex items-center justify-end gap-3
        px-6 py-4
        border-t border-arc-border
        bg-surface-tertiary
        flex-shrink-0
        ${className}
      `}
    >
      {children}
    </div>
  );
};

// Display names for debugging
Dialog.displayName = "Dialog";
DialogHeader.displayName = "DialogHeader";
DialogBody.displayName = "DialogBody";
DialogFooter.displayName = "DialogFooter";

// Attach compound components
Dialog.Header = DialogHeader;
Dialog.Body = DialogBody;
Dialog.Footer = DialogFooter;

// Named exports for compatibility
export { Dialog, DialogHeader, DialogBody, DialogFooter };
export default Dialog;
